import { useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowLeft, ArrowRight, Check, Users, PawPrint, Accessibility, BedDouble, CalendarDays, Images, ChevronLeft, ChevronRight, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Skeleton } from "@/components/ui/skeleton";
import { Reveal, Container, SpecList, PhotoMosaic, SectionTitle } from "@/components/common/Primitives";
import { DatePriceCalendar } from "@/components/booking/DatePriceCalendar";
import { RoomCard } from "@/components/rooms/RoomCard";
import { useLang } from "@/lib/i18n";
import { useContent, minPrice } from "@/lib/content";
import { useSearch } from "@/lib/search";
import { api } from "@/lib/api";
import { fmtDate, nightsBetween, nightsLabel, nok } from "@/lib/format";
import { BADGE_LABELS, buildBookingUrl } from "@/lib/booking";
import { usePageMeta } from "@/lib/seo";
import { FACILITIES } from "./Rooms";

const Lightbox = ({ images, name, idx, setIdx, open, onOpenChange }) => (
  <Dialog open={open} onOpenChange={onOpenChange}>
    <DialogContent className="max-w-6xl border-0 bg-[#0f1a24] p-3 text-white sm:p-5 [&>button]:hidden" data-testid="room-gallery-lightbox">
      <DialogTitle className="sr-only">{name}</DialogTitle>
      <DialogDescription className="sr-only">Galleri</DialogDescription>
      <div className="flex items-center justify-between pb-3">
        <span className="text-sm font-medium tabular text-white/70">{idx + 1} / {images.length}</span>
        <button type="button" onClick={() => onOpenChange(false)} className="inline-flex h-10 w-10 items-center justify-center rounded-full bg-white/10 text-white hover:bg-white/20" aria-label="Lukk" data-testid="lightbox-close"><X className="h-5 w-5" /></button>
      </div>
      <div className="relative">
        <img src={images[idx]} alt="" className="max-h-[74vh] w-full rounded-2xl object-contain" />
        <button type="button" onClick={() => setIdx((idx - 1 + images.length) % images.length)} className="absolute left-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-pop hover:bg-white/90" aria-label="Previous" data-testid="lightbox-prev"><ChevronLeft className="h-5 w-5" /></button>
        <button type="button" onClick={() => setIdx((idx + 1) % images.length)} className="absolute right-3 top-1/2 inline-flex h-11 w-11 -translate-y-1/2 items-center justify-center rounded-full bg-white text-foreground shadow-pop hover:bg-white/90" aria-label="Next" data-testid="lightbox-next"><ChevronRight className="h-5 w-5" /></button>
      </div>
    </DialogContent>
  </Dialog>
);

const Block = ({ title, children, testid, className = "" }) => (
  <section className={`border-t border-border pt-8 ${className}`} data-testid={testid}>
    <h2 className="text-xl font-bold tracking-[-0.02em] text-foreground sm:text-2xl">{title}</h2>
    <div className="mt-5">{children}</div>
  </section>
);

export default function RoomDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { lang, tx, tr } = useLang();
  const { data, isLoading } = useContent();
  const { search, setSearch } = useSearch();
  const rt = useMemo(() => (data?.room_types || []).find((r) => r.slug === slug), [data, slug]);
  const [guests, setGuests] = useState(2);
  const [board, setBoard] = useState("standard");
  const [calOpen, setCalOpen] = useState(false);
  const [lb, setLb] = useState(false);
  const [idx, setIdx] = useState(0);
  usePageMeta(rt ? tr(rt.name) : tx("Rom", "Room"), rt ? tr(rt.description) : "");
  useEffect(() => {
    if (rt) setGuests(Math.min(Math.max(1, search.adults + search.children), rt.max_guests));
  }, [rt]); // eslint-disable-line react-hooks/exhaustive-deps

  const hasDates = !!(search.ci && search.co);
  const avail = useQuery({
    queryKey: ["availability", search.ci, search.co, guests],
    queryFn: async () => (await api.get("/availability", { params: { check_in: search.ci, check_out: search.co, guests } })).data,
    enabled: hasDates,
  });
  const av = avail.data?.room_types?.find((a) => a.room_type_id === rt?.id);
  const total = av ? (board !== "standard" ? av.board_quotes?.[board]?.subtotal : av.quotes?.[String(guests)]?.subtotal) : null;

  if (isLoading) {
    return (
      <Container className="py-10" data-testid="room-detail-loading">
        <Skeleton className="h-5 w-24" /><Skeleton className="mt-4 h-10 w-1/2" />
        <Skeleton className="mt-6 h-[440px] w-full rounded-2xl" />
        <div className="mt-10 grid gap-10 lg:grid-cols-12"><div className="space-y-4 lg:col-span-7"><Skeleton className="h-6 w-full" /><Skeleton className="h-6 w-5/6" /></div><Skeleton className="h-80 lg:col-span-4 lg:col-start-9" /></div>
      </Container>
    );
  }
  if (!rt) {
    return (
      <Container className="py-24" data-testid="room-not-found">
        <p className="text-sm font-medium text-muted-foreground">404</p>
        <h1 className="mt-3 text-4xl font-bold tracking-[-0.03em] text-foreground">{tx("Fann ikkje rommet.", "Room not found.")}</h1>
        <Button asChild className="mt-8"><Link to="/rom">{tx("Til alle rom", "All rooms")}</Link></Button>
      </Container>
    );
  }

  const building = (data?.buildings || []).find((b) => b.id === rt.building);
  const byBuilding = Object.fromEntries((data?.buildings || []).map((b) => [b.id, b]));
  const others = (data?.room_types || []).filter((r) => r.id !== rt.id).slice(0, 3);
  const n = nightsBetween(search.ci, search.co);
  const images = rt.images || [];
  const highlights = rt.highlights?.[lang] || rt.highlights?.nn || [];
  const goBook = () => navigate(buildBookingUrl({ ...search, cart: [{ room_type_id: rt.id, guests, board }], step: hasDates ? 3 : 1 }));
  const open = (i) => { setIdx(i); setLb(true); };

  const specs = [
    { label: tx("Hus", "House"), value: tr(building?.name) },
    { label: tx("Gjester", "Guests"), value: tx(`${rt.base_guests} i standard${rt.max_extra_beds ? ` · inntil ${rt.max_guests} med ekstraseng` : ""}`, `${rt.base_guests} standard${rt.max_extra_beds ? ` · up to ${rt.max_guests} with extra bed` : ""}`) },
    rt.max_extra_beds > 0 && { label: tx("Ekstraseng", "Extra bed"), value: `${nok(rt.extra_bed_price, lang)} ${tx("per seng per natt", "per bed per night")}` },
    { label: tx("Hund", "Dogs"), value: rt.dogs_allowed ? tx("Velkomen", "Welcome") : tx("Ikkje i dette huset – vel Elvafløy", "Not in this house – choose the River Wing") },
    { label: tx("Trapper", "Stairs"), value: rt.step_free ? tx("Utan trapper", "Step-free access") : tx("Spør oss om rom utan trapper", "Ask us about step-free rooms") },
    { label: tx("Frukost", "Breakfast"), value: tx("Inkludert · 06.00–10.00", "Included · 06:00–10:00") },
    { label: tx("Innsjekk / utsjekk", "Check-in / out"), value: "15.00 / 12.00" },
  ];

  const fieldLabel = "block text-xs font-medium text-muted-foreground";

  return (
    <div data-testid="room-detail-page" className="pb-24 lg:pb-0">
      <Container className="pt-6 lg:pt-8">
        <Link to="/rom" className="inline-flex items-center gap-1.5 text-sm font-medium text-muted-foreground transition-colors hover:text-foreground" data-testid="room-back-link"><ArrowLeft className="h-4 w-4" /> {tx("Alle rom", "All rooms")}</Link>
        <div className="mt-4 flex flex-wrap items-end justify-between gap-4" data-testid="room-hero">
          <div>
            <p className="text-sm font-medium text-muted-foreground">{tr(building?.name)}</p>
            <h1 className="mt-1 text-[2rem] font-bold leading-[1.05] tracking-[-0.03em] text-foreground sm:text-[2.75rem]">{tr(rt.name)}</h1>
            <div className="mt-3 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-muted-foreground">
              <span className="inline-flex items-center gap-1.5"><Users className="h-4 w-4" strokeWidth={1.75} /> {tx(`Inntil ${rt.max_guests} gjester`, `Up to ${rt.max_guests} guests`)}</span>
              <span className="inline-flex items-center gap-1.5"><BedDouble className="h-4 w-4" strokeWidth={1.75} /> {rt.max_extra_beds > 0 ? tx(`Plass til ${rt.max_extra_beds} ekstraseng`, `Room for ${rt.max_extra_beds} extra bed${rt.max_extra_beds > 1 ? "s" : ""}`) : tx("Faste senger", "Fixed beds")}</span>
              {rt.dogs_allowed && <span className="inline-flex items-center gap-1.5"><PawPrint className="h-4 w-4" strokeWidth={1.75} /> {tx("Hund velkomen", "Dogs welcome")}</span>}
              {rt.step_free && <span className="inline-flex items-center gap-1.5"><Accessibility className="h-4 w-4" strokeWidth={1.75} /> {tx("Utan trapper", "Step-free")}</span>}
              {(rt.badges || []).map((b) => BADGE_LABELS[b] && <span key={b} className="rounded-full bg-secondary px-2.5 py-1 text-xs font-medium text-foreground">{tr(BADGE_LABELS[b])}</span>)}
            </div>
          </div>
          {images.length > 1 && (
            <button type="button" onClick={() => open(0)} className="inline-flex h-10 items-center gap-2 rounded-full border border-border bg-card px-4 text-sm font-semibold text-foreground transition-colors hover:bg-secondary lg:hidden" data-testid="room-gallery-open">
              <Images className="h-4 w-4" /> {tx(`Sjå ${images.length} bilete`, `View ${images.length} photos`)}
            </button>
          )}
        </div>
        <div className="mt-6">
          <PhotoMosaic images={images} alt={tr(rt.name)} onOpen={open} label={tx(`Sjå alle ${images.length} bilete`, `Show all ${images.length} photos`)} testid="room-photo-mosaic" />
        </div>
      </Container>

      <Container className="grid gap-12 py-10 lg:grid-cols-12 lg:gap-10 lg:py-14">
        <div className="min-w-0 lg:col-span-7">
          <Reveal>
            <p className="text-xl font-semibold leading-snug tracking-tight text-foreground sm:text-2xl">{tr(rt.tagline)}</p>
            <p className="mt-4 max-w-[60ch] text-base leading-relaxed text-muted-foreground sm:text-lg" data-testid="room-description">{tr(rt.description)}</p>
          </Reveal>

          <Block title={tx("Det du får", "What you get")} className="mt-10">
            <ul className="grid gap-3 sm:grid-cols-2" data-testid="room-highlights">
              {highlights.map((h) => (
                <li key={h} className="flex items-start gap-3 text-[0.9375rem] text-foreground"><span className="mt-0.5 flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-accent text-accent-foreground"><Check className="h-3 w-3" strokeWidth={3} /></span>{h}</li>
              ))}
            </ul>
          </Block>

          <Block title={tx("Fakta", "Details")} className="mt-10">
            <SpecList rows={specs} testid="room-specs" />
          </Block>

          <Block title={tx("Prisar", "Rates")} className="mt-10">
            <div className="overflow-hidden rounded-2xl border border-border" data-testid="room-price-table">
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 bg-secondary px-5 py-3 text-xs font-medium text-muted-foreground sm:gap-x-14">
                <span>{tx("Per rom per natt", "Per room per night")}</span><span>{tx("Midtveke", "Weekday")}</span><span>{tx("Helg", "Weekend")}</span>
              </div>
              <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 border-t border-border px-5 py-4 text-[0.9375rem] sm:gap-x-14">
                <span className="text-foreground">{tx("1 person", "1 guest")}</span><span className="font-semibold tabular text-foreground">{nok(rt.prices.weekday.p1, lang)}</span><span className="font-semibold tabular text-foreground">{nok(rt.prices.weekend.p1, lang)}</span>
              </div>
              {rt.max_guests > 1 && (
                <div className="grid grid-cols-[1fr_auto_auto] gap-x-8 border-t border-border px-5 py-4 text-[0.9375rem] sm:gap-x-14">
                  <span className="text-foreground">{tx("2 personar", "2 guests")}</span><span className="font-semibold tabular text-foreground">{nok(rt.prices.weekday.p2, lang)}</span><span className="font-semibold tabular text-foreground">{nok(rt.prices.weekend.p2, lang)}</span>
                </div>
              )}
              {(rt.board_options || []).map((bo) => (
                <div key={bo.id} className="grid grid-cols-[1fr_auto] gap-x-8 border-t border-border px-5 py-4 text-[0.9375rem] sm:gap-x-14">
                  <span className="text-foreground">{tr(bo.name)} <span className="text-muted-foreground">· {tx("per person per natt", "per person per night")}</span></span><span className="font-semibold tabular text-foreground">{nok(bo.weekday, lang)}</span>
                </div>
              ))}
            </div>
            <p className="mt-3 text-xs text-muted-foreground">{tx("Frukost og mva inkludert · helg = fredag og laurdag · betal på hotellet.", "Breakfast and VAT included · weekend = Friday & Saturday · pay at the hotel.")}</p>
          </Block>

          <Block title={tx("På rommet", "In the room")} className="mt-10">
            <ul className="grid gap-x-8 gap-y-3 sm:grid-cols-2" data-testid="room-facilities">
              {FACILITIES.slice(0, 8).map(({ icon: I, nn, en }) => (
                <li key={nn} className="flex items-center gap-3 text-[0.9375rem] text-foreground"><I className="h-[18px] w-[18px] shrink-0 text-muted-foreground" strokeWidth={1.75} />{tx(nn, en)}</li>
              ))}
            </ul>
          </Block>
        </div>

        {/* sticky booking card */}
        <aside className="lg:col-span-5 xl:col-span-4 xl:col-start-9">
          <div className="sticky top-24 rounded-[24px] border border-border bg-card p-6 shadow-float" data-testid="room-booking-widget">
            <div className="flex items-baseline gap-1.5">
              <span className="text-sm text-muted-foreground">{tx("frå", "from")}</span>
              <span className="text-2xl font-bold tabular tracking-tight text-foreground" data-testid="room-widget-from-price">{nok(minPrice(rt), lang)}</span>
              <span className="text-sm text-muted-foreground">/ {tx("natt", "night")}</span>
            </div>
            <p className="mt-1 text-xs text-muted-foreground">{tx("Frukost og mva inkludert. Betal på hotellet.", "Breakfast and VAT included. Pay at the hotel.")}</p>

            <div className="mt-5 overflow-hidden rounded-2xl border border-input">
              <Popover open={calOpen} onOpenChange={setCalOpen}>
                <PopoverTrigger asChild>
                  <button type="button" className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-secondary/70" data-testid="room-widget-dates">
                    <CalendarDays className="h-[18px] w-[18px] shrink-0 text-foreground" strokeWidth={1.75} />
                    <span className="min-w-0">
                      <span className={fieldLabel}>{tx("Datoar", "Dates")}</span>
                      <span className={`block truncate text-[0.9375rem] font-semibold tabular ${hasDates ? "text-foreground" : "text-foreground/55"}`}>{hasDates ? `${fmtDate(search.ci, lang)} – ${fmtDate(search.co, lang)} · ${nightsLabel(n, lang)}` : tx("Vel innsjekk og utsjekk", "Choose check-in and check-out")}</span>
                    </span>
                  </button>
                </PopoverTrigger>
                <PopoverContent align="end" sideOffset={10} className="w-[min(420px,94vw)] rounded-[24px] border-border bg-card p-5 shadow-pop" data-testid="room-widget-calendar">
                  <DatePriceCalendar ci={search.ci} co={search.co} guests={guests} months={1} onChange={(v) => { setSearch(v); if (v.ci && v.co) setTimeout(() => setCalOpen(false), 200); }} testid="room-widget-price-calendar" />
                </PopoverContent>
              </Popover>
              <div className="grid grid-cols-2 divide-x divide-input border-t border-input">
                <div className="px-4 py-3">
                  <p className={fieldLabel}>{tx("Gjester", "Guests")}</p>
                  <Select value={String(guests)} onValueChange={(v) => setGuests(parseInt(v, 10))}>
                    <SelectTrigger className="mt-0.5 h-7 border-0 bg-transparent p-0 text-[0.9375rem] font-semibold shadow-none focus:ring-0" data-testid="room-widget-guests"><SelectValue /></SelectTrigger>
                    <SelectContent className="rounded-xl">{Array.from({ length: rt.max_guests }, (_, i) => i + 1).map((g) => <SelectItem key={g} value={String(g)}>{g} {g === 1 ? tx("person", "guest") : tx("personar", "guests")}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div className="px-4 py-3">
                  <p className={fieldLabel}>{tx("Måltid", "Meals")}</p>
                  {(rt.board_options || []).length > 0 ? (
                    <Select value={board} onValueChange={setBoard}>
                      <SelectTrigger className="mt-0.5 h-7 border-0 bg-transparent p-0 text-[0.9375rem] font-semibold shadow-none focus:ring-0" data-testid="room-widget-board"><SelectValue /></SelectTrigger>
                      <SelectContent className="rounded-xl">
                        <SelectItem value="standard">{tx("Rom og frukost", "Room & breakfast")}</SelectItem>
                        {rt.board_options.map((bo) => <SelectItem key={bo.id} value={bo.id}>{tr(bo.name)}</SelectItem>)}
                      </SelectContent>
                    </Select>
                  ) : <p className="mt-0.5 h-7 text-[0.9375rem] font-semibold leading-7 text-foreground">{tx("Frukost inkl.", "Breakfast incl.")}</p>}
                </div>
              </div>
            </div>

            <div className="mt-5" data-testid="room-widget-price">
              {hasDates ? (
                avail.isLoading ? <p className="text-sm text-muted-foreground">{tx("Sjekkar ledige rom …", "Checking availability …")}</p> : av ? (
                  <>
                    <div className="flex items-baseline justify-between">
                      <span className="text-sm text-muted-foreground">{tx("Totalt", "Total")} · {nightsLabel(n, lang)}</span>
                      <span className="text-2xl font-bold tabular tracking-tight text-foreground" data-testid="room-widget-total">{nok(total, lang)}</span>
                    </div>
                    <p className={`mt-1 text-xs font-semibold ${av.available === 0 ? "text-destructive" : av.available <= 3 ? "text-warning" : "text-success"}`} data-testid="room-widget-availability">
                      {av.available === 0 ? tx("Fullt desse datoane – prøv andre datoar", "Sold out for these dates – try other dates") : av.available <= 3 ? tx(`Berre ${av.available} rom att`, `Only ${av.available} left`) : tx("Ledig", "Available")}
                    </p>
                  </>
                ) : <p className="text-sm text-muted-foreground">–</p>
              ) : <p className="text-sm leading-relaxed text-muted-foreground">{tx("Vel datoar for å sjå totalpris og om rommet er ledig.", "Choose dates to see the total and whether the room is available.")}</p>}
            </div>
            <Button variant="brand" size="xl" className="mt-5 w-full rounded-2xl" onClick={goBook} disabled={hasDates && av?.available === 0} data-testid="room-widget-book">
              {hasDates ? tx("Bestill dette rommet", "Book this room") : tx("Vel datoar og bestill", "Choose dates & book")} <ArrowRight className="h-4 w-4" />
            </Button>
            <ul className="mt-5 space-y-2 text-xs text-muted-foreground">
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-success" /> {tx("Betal på hotellet – inga forskotsbetaling", "Pay at the hotel – no prepayment")}</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-success" /> {tx("Gratis avbestilling til kl. 18 dagen før", "Free cancellation until 18:00 the day before")}</li>
              <li className="flex gap-2"><Check className="h-3.5 w-3.5 shrink-0 text-success" /> {tx("Beste pris når du bestiller direkte", "Best rate when booking direct")}</li>
            </ul>
          </div>
        </aside>
      </Container>

      <section className="border-t border-border py-16 lg:py-24" data-testid="room-others">
        <Container>
          <SectionTitle size="lg" title={tx("Andre rom", "Other rooms")} sub={tx("Kanskje passar eit av desse betre?", "Perhaps one of these suits you better?")} action={tx("Sjå alle rom", "See all rooms")} to="/rom" testid="room-others-title" />
          <div className="mt-10 grid gap-x-6 gap-y-10 sm:grid-cols-2 lg:grid-cols-3">
            {others.map((o) => <RoomCard key={o.id} rt={o} building={byBuilding[o.building]} testid={`room-other-${o.id}`} />)}
          </div>
        </Container>
      </section>

      {/* mobile bottom bar */}
      <div className="no-print fixed inset-x-0 bottom-0 z-40 border-t border-border bg-card/95 px-5 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))] backdrop-blur-xl lg:hidden" data-testid="room-mobile-bar">
        <div className="flex items-center justify-between gap-4">
          <div className="min-w-0">
            <p className="text-[0.9375rem] font-semibold text-foreground tabular">{hasDates && total ? nok(total, lang) : `${tx("frå", "from")} ${nok(minPrice(rt), lang)}`} <span className="text-sm font-normal text-muted-foreground">{hasDates && total ? `· ${nightsLabel(n, lang)}` : `/ ${tx("natt", "night")}`}</span></p>
            <p className="truncate text-sm text-muted-foreground">{hasDates ? `${fmtDate(search.ci, lang, "d. MMM")} – ${fmtDate(search.co, lang, "d. MMM")}` : tx("Frukost inkludert · betal på hotellet", "Breakfast included · pay at the hotel")}</p>
          </div>
          <Button variant="brand" size="lg" className="px-6" onClick={goBook} disabled={hasDates && av?.available === 0} data-testid="room-mobile-book">{tx("Bestill", "Book")}</Button>
        </div>
      </div>

      {images.length > 0 && <Lightbox images={images} name={tr(rt.name)} idx={idx} setIdx={setIdx} open={lb} onOpenChange={setLb} />}
    </div>
  );
}
